<template>
    <!-- Add camera controls -->
    <TresPerspectiveCamera :position="[0, 0, 50]" />
    <!-- Add lighting -->
    <TresPointLight
        :intensity="12"
        :decay="2"
        :position="[-0.2, 0.5, 1]"
        :receive-shadow="false"
        :cast-shadow="true"
        :shadow-camera-near="0.5"
        :shadow-camera-far="500"
    />

    <!-- Landscape planet labels -->
    <TresGroup>
        <Html
            class="slt-gsap-planet-label"
            transform
            v-for="(gt, idx) in globeTexts"
            :key="gt.text"
            :position="planetPositions[gt.planet] || [0, 0, 0]"
            :scale="configs.textScaleFactor"
            :distance-factor="10"
        >
            <div
                class="relative font-en text-xs text-white/30"
                :style="{ opacity: getOpacity(idx) }"
            >
                <span class="absolute -left-3 -top-3 text-base">+</span>
                <p>{{ gt.text }}</p>
                <span>{{
                    `+${Math.floor(getCurrentDegree(idx))}° ${gt.coordinate[1]}’`
                }}</span>
            </div>
        </Html>
    </TresGroup>
</template>

<script setup>
import { Vector3 } from "three";
import { map } from "~/lib/utils";
import { moleculesLandscapeExperienceAnimate, organismPlanetsAnimate } from "@/lib/gsap-about";
import { killAnimation } from "@/lib/gsap-core";
import { Html } from "@tresjs/cientos";
import { useTresContext, useLoop } from "@tresjs/core";
import PlanetModelsManager from "./about/PlanetsModelsManager";
import { gsap } from "gsap";
import { useGtag } from "vue-gtag-next";

// Get the Three.js scene
const { scene, camera } = useTresContext();
const { landscapesProgress } = useLandscapesProgress();
const { configs, mapWindowScale } = useExperience();
const { event } = useGtag();

// TODO: make this data from CMS
const globeTexts = ref([
    {
        planet: "red",
        text: "Digital Games",
        coordinate: [7, 48],
    },
    {
        planet: "greenRing",
        text: "News Media",
        coordinate: [7, 16],
    },
    {
        planet: "green",
        text: "Gamania Original",
        coordinate: [7, 8],
    },
    {
        planet: "earth",
        text: "Lifestyle",
        coordinate: [7, 36],
    },
    {
        planet: "yellow",
        text: "Corporate Services",
        coordinate: [7, 4],
    },
    {
        planet: "blueRing",
        text: "AI Innovation",
        coordinate: [7, 12],
    },
]);

const totalPlanets = globeTexts.value.length;
const totalDegrees = (360 / totalPlanets) * (totalPlanets - 1); // 300°
const step = totalDegrees / (totalPlanets - 1);

const getCurrentDegree = (idx) => {
    const cameraDeg = landscapesProgress.value * totalDegrees;
    const labelDeg = idx * step;
    return (cameraDeg - labelDeg + 360) % 360;
};

const getOpacity = (idx) => {
    const rel = getCurrentDegree(idx);
    const fadeWidth = 20; // 漸變區寬度 20度
    const hideStart = 180;
    const hideEnd = 270;

    if (rel <= hideStart - fadeWidth) {
        // 180度前完全顯示
        return 1;
    } else if (rel > hideStart - fadeWidth && rel < hideStart) {
        // 從 (180 - 20) 到 180 線性漸變 1 -> 0
        return 1 - (rel - (hideStart - fadeWidth)) / fadeWidth;
    } else if (rel >= hideStart && rel <= hideEnd) {
        // 180~270 之間 0
        return 0;
    } else if (rel > hideEnd && rel <= hideEnd + fadeWidth) {
        // 270度後漸變從 0 → 1
        return (rel - hideEnd) / fadeWidth;
    } else {
        // 250度後完全顯示
        return 1;
    }
};

const cameraProgress = ref(0);

let planetsManager = null;
const planetPositions = reactive({
    green: [0, 0, 0],
    earth: [0, 0, 0],
    blueRing: [0, 0, 0],
    red: [0, 0, 0],
    yellow: [0, 0, 0],
    greenRing: [0, 0, 0],
});

const { onBeforeRender, pauseRender, resumeRender } = useLoop();

onBeforeRender(({ delta, elapsed }) => {
    if (planetsManager) {
        planetsManager.animate(delta, elapsed, landscapesProgress.value);
        // Update positions for HTML labels
        updatePlanetPositions();
        if (cameraProgress.value > 1) {
            const disappearProgress = map(cameraProgress.value, 1, 2, 0, 1);
            planetsManager.animateDisappear(disappearProgress);
        } else {
            // Always ensure planets are fully visible when cameraProgress <= 1
            planetsManager.animateDisappear(0);
        }
    }
    if (camera.value) {
        // Map camera position based on progress value
        // 0-1: zoom in (50 to 5.5)
        // 1-2: zoom out (5.5 to 30)
        const zoomFactor = configs.value.zoomFactor;
        if (cameraProgress.value <= 1) {
            camera.value.position.z = map(
                cameraProgress.value,
                0,
                1,
                50,
                zoomFactor,
            );
        } else {
            camera.value.position.z = map(
                cameraProgress.value,
                1,
                2,
                zoomFactor,
                15,
            );
        }
    }
});

let tl = null;
let tl2 = null;
onMounted(async () => {
    tl2 = organismPlanetsAnimate(pauseRender, resumeRender);

    // Initialize and load the planet models
    planetsManager = new PlanetModelsManager(scene.value);
    await planetsManager.loadModels();

    // Initialize positions
    updatePlanetPositions();
    tl = moleculesLandscapeExperienceAnimate(cameraProgress, () =>
        event("user_engagement", {
            event_category: "about",
            event_label: "集團版圖",
        }),
    );

    globeTexts.value.forEach((planet) => {
        // Animate the coordinate[1] value
        gsap.to(planet.coordinate, {
            duration: 1000, // Duration of the animation
            1: 180, // Target value for the rotation indicator
            roundProps: "1", // Round the value to the nearest integer
            repeat: -1,
            ease: "power2.out", // Easing function for smoothness
        });
    });
});

// Function to update planet positions for labels
function updatePlanetPositions() {
    if (!planetsManager) return;

    Object.keys(planetPositions).forEach((name) => {
        const model = planetsManager.getModel(name);
        if (model) {
            // Get the world position of the model
            const position = new Vector3();
            model.getWorldPosition(position);

            // Update the reactive positions
            planetPositions[name] = [
                position.x + mapWindowScale(0.8, 0.5),
                position.y,
                position.z,
            ];
        }
    });
}
onBeforeUnmount(() => {
    planetsManager.destroy();
    killAnimation(tl);
    killAnimation(tl2);
});
</script>
